import React from 'react';
import { Tooltip, Popover } from 'antd';
import { LockOutlined, InfoCircleOutlined } from '@ant-design/icons';

/**
 * Info dot — an ⓘ that opens a popover explaining what a field does and what
 * editing it implies. `title` is the field, `body` the (accurate) explanation.
 */
export const InfoDot: React.FC<{ title: string; body: React.ReactNode }> = ({
  title,
  body,
}) => (
  <Popover
    title={title}
    content={<div style={{ maxWidth: 300, fontSize: 13 }}>{body}</div>}
    trigger="click"
    placement="left"
  >
    <InfoCircleOutlined
      className="devadmin-info"
      onClick={(e) => e.stopPropagation()}
    />
  </Popover>
);

/** Label-over-control field row used across the inspectors. */
export const Field: React.FC<{
  label: string;
  hint?: string;
  info?: React.ReactNode;
  children: React.ReactNode;
}> = ({ label, hint, info, children }) => (
  <div className="devadmin-field">
    <div className="devadmin-field__label">
      {label}
      {hint && <span className="devadmin-field__hint">{hint}</span>}
      {info && <InfoDot title={label} body={info} />}
    </div>
    <div className="devadmin-field__control">{children}</div>
  </div>
);

/** Titled inspector section. */
export const Section: React.FC<{
  title: string;
  hint?: string;
  info?: React.ReactNode;
  extra?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, hint, info, extra, children }) => (
  <div className="devadmin-insp__section">
    <div className="devadmin-insp__section-title">
      <span>
        {title}
        {hint && <span className="devadmin-insp__section-hint"> · {hint}</span>}
        {info && <InfoDot title={title} body={info} />}
      </span>
      {extra}
    </div>
    {children}
  </div>
);

/** A read-only, locked value with a "why it's locked" tooltip (transparency). */
export const LockedRow: React.FC<{ label: string; value: string; why: string }> = ({
  label,
  value,
  why,
}) => (
  <div className="devadmin-locked">
    <span className="devadmin-locked__label">
      <LockOutlined /> {label}
    </span>
    <Tooltip title={why}>
      <span className="devadmin-locked__value">{value}</span>
    </Tooltip>
  </div>
);
